import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { usePlaces } from '../../hooks';
import type { PlaceType } from '../../types';

const getTypeColor = (type: PlaceType) => {
  switch (type) {
    case 'restaurant': return 'primary';
    case 'hospital': return 'error';
    case 'hairshop': return 'secondary';
    case 'nailshop': return 'warning';
    case 'accommodation': return 'success';
    default: return 'default';
  }
};

export function PlaceListPage() {
  const navigate = useNavigate();
  const { places, isLoading, error, fetchPlaces } = usePlaces();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [placeType, setPlaceType] = useState<PlaceType | ''>('');
  const [sortBy, setSortBy] = useState<'created_at' | 'gdid_score2' | 'name'>('created_at');

  const loadData = useCallback(() => {
    fetchPlaces({
      page: page + 1,
      limit: rowsPerPage,
      search: search || undefined,
      place_type: placeType || undefined,
      sort: sortBy,
      order: 'desc',
    });
  }, [page, rowsPerPage, search, placeType, sortBy, fetchPlaces]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleRowClick = (id: number) => {
    navigate(`/places/${id}`);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
          플레이스 관리
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/places/new')}
        >
          새 플레이스 등록
        </Button>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="검색 (이름, 주소)"
            variant="outlined"
            size="small"
            value={search}
            onChange={handleSearchChange}
            sx={{ minWidth: 300, bgcolor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'white' }}>
            <InputLabel>업종 분류</InputLabel>
            <Select
              value={placeType}
              label="업종 분류"
              onChange={(e) => setPlaceType(e.target.value as PlaceType)}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="restaurant">음식점</MenuItem>
              <MenuItem value="hospital">병원</MenuItem>
              <MenuItem value="hairshop">헤어샵</MenuItem>
              <MenuItem value="nailshop">네일샵</MenuItem>
              <MenuItem value="accommodation">숙박</MenuItem>
              <MenuItem value="place">기타</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'white' }}>
            <InputLabel>정렬</InputLabel>
            <Select
              value={sortBy}
              label="정렬"
              onChange={(e) => setSortBy(e.target.value as 'created_at' | 'gdid_score2' | 'name')}
            >
              <MenuItem value="created_at">최신순</MenuItem>
              <MenuItem value="gdid_score2">GDID 점수순</MenuItem>
              <MenuItem value="name">이름순</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: 800 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>플레이스명</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>업종</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>지역</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }} align="right">GDID Score 2</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }} align="right">Rank &lt; 6</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>등록일</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : places?.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography color="textSecondary">데이터가 없습니다.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                places?.items.map((place) => (
                  <TableRow
                    key={place.id}
                    hover
                    onClick={() => handleRowClick(place.id)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f5' } }}
                  >
                    <TableCell sx={{ color: '#868e96' }}>{place.id}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{place.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={place.place_type.toUpperCase()}
                        size="small"
                        color={getTypeColor(place.place_type)}
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>{place.region}</TableCell>
                    <TableCell align="right">
                      <Box sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.5,
                        bgcolor: place.gdid_score2 ? 'rgba(0,0,0,0.04)' : 'transparent',
                        borderRadius: 1,
                        fontWeight: 600
                      }}>
                        {place.gdid_score2?.toLocaleString() ?? '-'}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{place.keyword_count_rank_under_6}</TableCell>
                    <TableCell sx={{ color: '#868e96' }}>
                      {new Date(place.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={places?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>
    </Container>
  );
}
